// Pure helpers for the Website RMA rate-quote/label-deduction flow
// (SuggestionPanel.jsx/ManualActionsSidebar.jsx's "Return shipping — who
// pays?" section) — split out of SuggestionPanel.jsx (which re-exports them
// for backward compatibility) so they're unit-testable under plain
// `node --test`, which can't import a .jsx file directly.

// Rounds a money value to the cent — combineRateQuotes()/
// shippingRateDefaultDeduction() below both sum floating-point rate-quote
// fields, which drifts (e.g. 68.7299999999999) without this.
export function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

// The deduction amount each "who pays" option defaults the editable field
// to — customerCost/ourCost read straight off the rate quote; a customer
// shipping their own label has nothing to deduct at all.
export function shippingRateDefaultDeduction(option, rateQuote) {
  if (option === 'ourCost') return rateQuote?.totalNegotiated != null ? roundMoney(rateQuote.totalNegotiated) : null
  if (option === 'customerCost') return rateQuote?.totalEstimated != null ? roundMoney(rateQuote.totalEstimated) : null
  return null
}

// Reduces a per-package rateQuotes array (feature/return-label-multi-package)
// down to one combined quote shaped exactly like a single rate quote, so
// every existing shippingRateDefaultDeduction()/shippingHighCostPct()/
// shippingDeductionNoteLine()/shippingNoteBlock() call above can keep
// operating on "the quote" unchanged — the combined total IS the SUM ACROSS
// ALL PACKAGES of each field, matching what those callers need ("the default
// label deduction becomes the sum across all packages of the chosen field").
// null when there are no quotes at all (no rate-quote flow applies).
export function combineRateQuotes(rateQuotes) {
  if (!rateQuotes?.length) return null
  return {
    totalEstimated: roundMoney(rateQuotes.reduce((sum, q) => sum + (Number(q?.totalEstimated) || 0), 0)),
    totalNegotiated: roundMoney(rateQuotes.reduce((sum, q) => sum + (Number(q?.totalNegotiated) || 0), 0)),
  }
}

// PR #1151 (merged 2026-09-22) added unitCost to buildAllItems() server-side
// — a suggestion generated BEFORE that has itemSelection.allItems without
// it, so computeUnitRefundAmounts() (refundAmount.js) returns null for every
// affected line (most visibly a kit's own components, since a missing/zero
// unitCost on the KIT line blocks all of them at once). Backfills a falsy
// unitCost from the live order's own per-item data (liveItems — the Items
// widget's own fetch, GET .../order-summary/items), matched by sku +
// lineSequence, same convention as every other "never guess, verify against
// the live order" rule in this codebase. liveItems null/empty (Item Info not
// enabled, or nothing loaded yet) is a no-op — the existing unresolvedSkus
// warning still applies exactly as before.
export function fillMissingUnitCost(allItems, liveItems) {
  if (!liveItems?.length) return allItems
  return (allItems ?? []).map((it) => {
    if (it.unitCost) return it
    const live = liveItems.find((li) => li.sku === it.sku && li.lineSequence === it.lineSequence)
    return live?.unitCost ? { ...it, unitCost: live.unitCost } : it
  })
}
